import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch } from 'react-redux';

import { fetchContactsAPI } from '@sophocles/api/contacts';
import PrimaryContactConfirmDialog from '@sophocles/components/contacts/PrimaryContactConfirmDialog';
import UsageHistoryView from '@sophocles/components/contacts/UsageHistoryView';
import useContactUsageHistory from '@sophocles/components/contacts/useContactUsageHistory';
import StatusChip from '@sophocles/components/StatusChip';
import { deleteContact, restoreContact } from '@sophocles/features/contacts/api';
import { addToast } from '@sophocles/features/toast';
import { buildContactQueryParams } from '@sophocles/features/warehouse/contactQueryParams';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import { formatPhoneDisplay } from '@sophocles/utils/phoneUtils';

/**
 * ContactCardList - Mobile view with card-based infinite scroll
 */
const ContactCardList = ({
  customerId,
  siteGroupId,
  showDeleted,
  onEditContact,
  scopeFilter = 'all',
  searchQuery = '',
  filters = {},
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [contacts, setContacts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [customerPage, setCustomerPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Primary contact dialog state
  const [primaryDialogOpen, setPrimaryDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  // Usage history dialog state
  const usageHistory = useContactUsageHistory();

  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const PAGE_SIZE = 20;

  // Helper function to determine contact scope
  const getContactScope = useCallback(contact => {
    // Customer scope: has customer FK but NO site_group FK
    if (contact.customer && !contact.site_group) {
      return 'customer';
    }
    // Site Group scope: has site_group FK
    if (contact.site_group) {
      return 'site_group';
    }
    return 'unknown';
  }, []);

  // Helper function to check if contact is inherited (customer-level when viewing from site group)
  const isInheritedContact = useCallback(
    contact => {
      return siteGroupId && scopeFilter === 'all' && getContactScope(contact) === 'customer';
    },
    [siteGroupId, scopeFilter, getContactScope],
  );

  // Load initial contacts
  const loadInitialContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: 0,
        not_deleted: !showDeleted,
        ...buildContactQueryParams(filters, { customerId, siteGroupId, scopeFilter, searchQuery }),
      };

      const response = await fetchContactsAPI(params);
      const allContacts = response.results || response;

      setContacts(allContacts);
      setHasMore(response.next !== null);
      setCustomerPage(1);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      dispatch(addToast({ message: t('contacts.management.loadError', 'Failed to load contacts'), level: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [customerId, siteGroupId, showDeleted, scopeFilter, searchQuery, filters, t, dispatch]);

  // Load more contacts for infinite scroll
  const loadMoreContacts = async () => {
    // Early exit if no more data to fetch
    if (!hasMore) {
      return;
    }

    try {
      const params = {
        limit: PAGE_SIZE,
        offset: customerPage * PAGE_SIZE,
        not_deleted: !showDeleted,
        ...buildContactQueryParams(filters, { customerId, siteGroupId, scopeFilter, searchQuery }),
      };

      const response = await fetchContactsAPI(params);
      const newContacts = response.results || response;

      setContacts(prev => [...prev, ...newContacts]);
      setHasMore(response.next !== null);
      setCustomerPage(prev => prev + 1);
    } catch (error) {
      console.error('Failed to load more contacts:', error);
      dispatch(addToast({ message: t('contacts.management.loadError'), level: 'error' }));
    }
  };

  // Initial load
  useEffect(() => {
    loadInitialContacts();
  }, [loadInitialContacts]);

  const handlePrimaryDialogClose = () => {
    setPrimaryDialogOpen(false);
    setSelectedContact(null);
  };

  const handlePrimarySuccess = () => {
    loadInitialContacts(); // Reload to reflect changes
    handlePrimaryDialogClose();
  };

  // Handle delete
  const handleDelete = async contactId => {
    try {
      await dispatch(deleteContact(contactId)).unwrap();
      dispatch(addToast({ message: t('contacts.management.deleteSuccess'), level: 'success' }));
      loadInitialContacts();
    } catch (error) {
      dispatch(addToast({ message: t('contacts.management.deleteError'), level: 'error' }));
    }
  };

  // Handle restore
  const handleRestore = async contactId => {
    try {
      await dispatch(restoreContact(contactId)).unwrap();
      dispatch(addToast({ message: t('contacts.management.restoreSuccess'), level: 'success' }));
      loadInitialContacts();
    } catch (error) {
      dispatch(addToast({ message: t('contacts.management.restoreError'), level: 'error' }));
    }
  };

  // Format address
  const formatAddress = contact => {
    const { address, city, state, zipcode } = contact;
    if (!address) return null;

    const parts = [address];
    if (city) parts.push(city);
    if (state) parts.push(state);
    if (zipcode) parts.push(zipcode);

    return parts.join(', ');
  };

  // Render empty state
  if (!loading && contacts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t('contacts.management.noContacts')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {deleteConfirmDialog}
      <InfiniteScroll
        dataLength={contacts.length}
        next={loadMoreContacts}
        hasMore={hasMore}
        loader={
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={32} />
          </Box>
        }
        endMessage={
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('contacts.management.allLoaded')}
            </Typography>
          </Box>
        }
      >
        {contacts.map(contact => {
          const isDeleted = !!contact.deleted_at;
          const isRmaBilling = contact.is_primary_rma_billing;
          const isRmaShipping = contact.is_primary_rma_shipping;
          const isSalesBilling = contact.is_primary_sales_billing;
          const isSalesShipping = contact.is_primary_sales_shipping;
          const isPrimary = isRmaBilling || isRmaShipping || isSalesBilling || isSalesShipping; // For border highlight
          const isInherited = isInheritedContact(contact);
          const contactScope = getContactScope(contact);
          const addressText = formatAddress(contact);

          return (
            <Card
              key={contact.id}
              sx={{
                mb: 2,
                borderLeft: isPrimary ? 4 : 0,
                borderLeftColor: 'warning.main',
                opacity: isDeleted ? 0.6 : isInherited ? 0.7 : 1,
                fontStyle: isInherited ? 'italic' : 'normal',
              }}
            >
              <CardContent>
                {/* Header: Name + Primary Badges + Inherited/Deleted Chips */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1, gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6">{contact.name}</Typography>
                    {isRmaBilling && (
                      <Chip
                        icon={<StarIcon />}
                        label={t('contacts.primary.rmaBilling', 'RMA Billing')}
                        size="small"
                        color="primary"
                        sx={{ height: 24, fontSize: '0.75rem' }}
                      />
                    )}
                    {isRmaShipping && (
                      <Chip
                        icon={<StarIcon />}
                        label={t('contacts.primary.rmaShipping', 'RMA Shipping')}
                        size="small"
                        color="secondary"
                        sx={{ height: 24, fontSize: '0.75rem' }}
                      />
                    )}
                    {isSalesBilling && (
                      <Chip
                        icon={<StarIcon />}
                        label={t('contacts.primary.salesBilling', 'Sales Billing')}
                        size="small"
                        color="success"
                        sx={{ height: 24, fontSize: '0.75rem' }}
                      />
                    )}
                    {isSalesShipping && (
                      <Chip
                        icon={<StarIcon />}
                        label={t('contacts.primary.salesShipping', 'Sales Shipping')}
                        size="small"
                        color="warning"
                        sx={{ height: 24, fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isInherited && (
                      <Chip
                        label={t('contacts.scope.inherited', 'Inherited')}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    {isDeleted && <StatusChip label={t('contacts.status.deleted', 'Deleted')} color="bad" />}
                  </Box>
                </Box>

                {/* Business Name */}
                {contact.business_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {contact.business_name}
                    </Typography>
                  </Box>
                )}

                {/* Email */}
                {contact.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {contact.email}
                    </Typography>
                  </Box>
                )}

                {/* Phone */}
                {contact.phone1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {formatPhoneDisplay(contact.phone1)}
                    </Typography>
                  </Box>
                )}

                {/* Address */}
                {addressText && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {addressText}
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                {/* Usage History Button */}
                <Button
                  startIcon={<HistoryIcon />}
                  onClick={() => usageHistory.open(contact)}
                  size="small"
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  {t('contacts.usageHistory.viewHistory', 'History')}
                </Button>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* Disable actions for inherited contacts */}
                  {isInherited ? (
                    <Tooltip
                      title={t(
                        'contacts.scope.inheritedTooltip',
                        'This contact is inherited from the customer level and cannot be edited here',
                      )}
                    >
                      <span>
                        <Button startIcon={<EditIcon />} disabled sx={{ minWidth: 44, minHeight: 44 }}>
                          {t('actions.edit', 'Edit')}
                        </Button>
                        <Button startIcon={<DeleteIcon />} disabled sx={{ minWidth: 44, minHeight: 44 }}>
                          {t('actions.delete', 'Delete')}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      {!isDeleted && (
                        <>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => onEditContact(contact)}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            {t('actions.edit', 'Edit')}
                          </Button>
                          <Button
                            startIcon={<DeleteIcon />}
                            color="error"
                            onClick={() =>
                              confirmDelete(contact, {
                                entityLabel: t('general.contact'),
                                onConfirm: deletedContact => handleDelete(deletedContact.id),
                              })
                            }
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            {t('actions.delete', 'Delete')}
                          </Button>
                        </>
                      )}
                      {isDeleted && (
                        <Button
                          startIcon={<RestoreIcon />}
                          color="primary"
                          onClick={() => handleRestore(contact.id)}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          {t('actions.restore', 'Restore')}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </CardActions>
            </Card>
          );
        })}
      </InfiniteScroll>

      {/* Primary Contact Dialog */}
      <PrimaryContactConfirmDialog
        open={primaryDialogOpen}
        contact={selectedContact}
        onClose={handlePrimaryDialogClose}
        onSuccess={handlePrimarySuccess}
      />

      {/* Usage History Dialog */}
      <Dialog open={usageHistory.isOpen} onClose={usageHistory.close} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">{t('contacts.usageHistory.title', 'Usage History')}</Typography>
          </Box>
          {usageHistory.contact && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {usageHistory.contact.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {usageHistory.contact && (
            <UsageHistoryView
              usageData={usageHistory.usageData}
              loading={usageHistory.loading}
              error={usageHistory.usageData?.error}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={usageHistory.close}>{t('actions.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ContactCardList.propTypes = {
  customerId: PropTypes.number,
  siteGroupId: PropTypes.number,
  showDeleted: PropTypes.bool.isRequired,
  onEditContact: PropTypes.func.isRequired,
  scopeFilter: PropTypes.oneOf(['all', 'customer', 'site_group']),
  searchQuery: PropTypes.string,
  filters: PropTypes.object,
};

export default ContactCardList;
